import React, { useMemo, useState, useRef, useEffect } from "react";
import { CasuistryAction } from "../../../../entities/company/Assistant/Assistant";
import { TranslationService } from "../../../../services/TranslationService";
import { Editor as EmailEditor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import SwitchButton from "../../../shared/components/SwitchButton";
import MultiFileUploadNew from "../../../shared/components/MultiFileUploadNew";
import { ToolbarOptions } from "../../components/ToolbarOptions";
import { EditorType } from "../../../company/Configuration/EmailTemplateEdit";
import { Cast } from "../../../../utils/Utils";
import { formatDateDigits } from "../../../../utils/FormatUtils";
import { parseDate } from "../../../../utils/ParseUtils";
import UsecasesService from "../../../../services/UsecasesService";
import ActivityService, {
  EmailInvoiceListType,
  SetEmailRequest
} from "../../../../services/ActivityService";
import CompanyService, {
  Entities,
  DataType
} from "../../../../services/CompanyService";
import { ToastService } from "../../../shared/bootstrap/Toast";
import Select from "../../../shared/components/Select";
import { InvoiceChangeStatusForm } from "../../invoice/InvoiceChangeStatusForm";
import { InvoiceEditStatus } from "../../invoice/entities/InvoiceEditStatus";
import TooltipComponent from "../../../shared/TooltipComponent";
import { SafeHtml } from "../../../../components/SafeHtml";
import "../../../shared/components/floatingStyles.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

/* ========= Tipos ========= */
type DetectedCategory = {
  code: number;
  type: string;
  reason: string;
  confidence?: number;
  confidenceReason?: string;
};

type InvoiceStatusRow = {
  invoice: string;
  statusId: number | string;
  additionalValues?: { valueId: string | number; valueLabel: string }[];
  // Campos adicionales opcionales que pueden venir del servicio
  amount?: number;
  pending?: number;
  dueDate?: string;
  emittedDate?: string;
  description?: string;
  currencyId?: number;
  [key: string]: any; // Para permitir otras propiedades adicionales
};

type Task = {
  responsible: string; // puede venir email o identificador
  expirationDate?: string; // ISO
  description: string;
};

type AIResponse = {
  info?: string;
  emailId?: number;
  detectedCategories?: DetectedCategory[];
  invoiceStatus?: InvoiceStatusRow[];
  action?: string | null;
  mailTemplateId?: string | null;
  responseMail?: string | null;
  emailSubject?: string | null;
  senderEmail?: string | null;
  emailAttachments?: Array<{ id: string; name: string }>;
  includeInvoices?: boolean;
  includePortalLink?: boolean;
  tasks?: Task[];
  dataFiles?: string | null;
  countFiles?: number | null;
  tokens?: number;
  cost?: number;
  [key: string]: any; // Para permitir otras propiedades adicionales del objeto original
};

type TemplateMeta = {
  id: string;
  name?: string;
  subject?: string;
  body: string;
  includesPendingList?: boolean;
};

type StatusMeta = {
  id: number | string;
  name: string;
  fields?: { id: string; label: string }[];
};

type Props = {
  json: AIResponse;
  personId?: number; // ID del cliente/persona para la preview

  /* Parte 2 */
  resolveTemplate?: (
    id: string
  ) => Promise<TemplateMeta | null> | TemplateMeta | null;
  outgoingAttachments?: number;
  onOpenViewEmail?: (payload: {
    templateId?: string | null;
    subject?: string;
    body: string;
    source: "template" | "ai";
  }) => void;
  onOpenEditEmail?: (payload: {
    templateId?: string | null;
    subject?: string;
    body: string;
    source: "template" | "ai";
  }) => void;

  /* Parte 3 */
  resolveStatusMeta?: (statusId: number | string) => StatusMeta | null;
  onOpenViewStatusTable?: (
    rows: Array<{
      invoice: string;
      statusName: string;
      fields: { id: string; label: string; value?: string }[];
    }>
  ) => void;
  onChangeInvoiceStatus?: (row: InvoiceStatusRow) => void;

  /* Parte 4 */
  /** Mapear el {responsible} a un nombre interno visible (p.ej. email → "Arias, Gastón"). */
  resolveUserName?: (responsible: string) => string | null;
  /** Abrir modal "Nueva Tarea" para editar/crear a partir de la recomendación. */
  onEditTask?: (task: Task) => void;

  /* Parte 5: Confirmación */
  /** Callback para cuando se confirma el análisis de IA. Recibe el objeto JSON completo. */
  onConfirm?: (json: AIResponse) => void | Promise<void>;
  /** Callback para cancelar/cerrar el panel. Si se provee, muestra botón Cancelar en el footer. */
  onCancel?: () => void;

  /** Configuración de acciones por casuística. Pre-setea los switches según el código detectado. */
  casuistryActions?: CasuistryAction[];
};

/* ========= Estilos ========= */
const box: React.CSSProperties = {
  background: "transparent",
  borderRadius: 10,
  padding: 16
};
const title: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 700 };
const row: React.CSSProperties = { marginTop: 12, lineHeight: 1.45 };
const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  borderRadius: 8,
  overflow: "hidden"
};
const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 13,
  color: "#667085",
  background: "#fafafa"
};
const td: React.CSSProperties = {
  padding: "12px",
  borderBottom: "1px solid #f1f3f5",
  color: "#101828",
  verticalAlign: "middle"
};
const switchWrap: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  userSelect: "none",
  color: "#475467",
  fontWeight: 700
};

/* ========= Iconos ========= */
const EyeIcon = () => <i className="far fa-eye" style={{ fontSize: 14 }} />;
const PencilIcon = () => <i className="far fa-pen" style={{ fontSize: 14 }} />;
const TrashIcon = () => <i className="far fa-trash" style={{ fontSize: 14 }} />;
const CloseIcon = () => <i className="fas fa-times" style={{ fontSize: 14 }} />;

/* ========= Auxiliares ========= */
const Empty: React.FC<{ text?: string }> = ({ text = "—" }) => (
  <span style={{ color: "#98A2B3" }}>{text}</span>
);
const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleDateString("es-AR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      })
    : "";
const truncate = (s: string, n = 80) =>
  s.length > n ? s.slice(0, n - 1) + "…" : s;

/* ========= Subcomps Parte 1 ========= */
const CategoryCard: React.FC<{ cat: DetectedCategory; idx: number }> = ({
  cat,
  idx
}) => {
  // Concatenar Casuística: {type} - {reason}
  const casuisticaText = cat.type
    ? cat.reason
      ? `${cat.type} - ${cat.reason}`
      : cat.type
    : cat.reason || "";

  // Concatenar Confianza: {confidence} - {confidenceReason}
  const confianzaText =
    typeof cat.confidence === "number"
      ? cat.confidenceReason
        ? `${cat.confidence}% - ${cat.confidenceReason}`
        : `${cat.confidence}%`
      : "";

  return (
    <div style={{ borderRadius: 8, marginTop: idx > 0 ? 12 : 16 }}>
      {/* Casuística */}
      <div style={{ marginBottom: 16, lineHeight: 1.6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#475467" }}>
          Casuística:{" "}
        </span>
        <span style={{ fontSize: 13, color: "#101828" }}>
          {casuisticaText || <Empty />}
        </span>
      </div>

      {/* Confianza */}
      {confianzaText && (
        <div style={{ marginBottom: 16, lineHeight: 1.6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#475467" }}>
            Confianza:{" "}
          </span>
          <span style={{ fontSize: 13, color: "#101828" }}>
            {confianzaText}
          </span>
        </div>
      )}
    </div>
  );
};

/* ========= Componente principal ========= */
const AIAgentEvaluatePopup: React.FC<Props> = ({
  json,
  personId,

  /* Parte 2 */
  resolveTemplate,
  outgoingAttachments = 0,
  onOpenEditEmail,
  onOpenViewEmail,

  /* Parte 3 */
  resolveStatusMeta,
  onOpenViewStatusTable,
  onChangeInvoiceStatus,

  /* Parte 4 */
  resolveUserName,
  onEditTask,

  /* Parte 5 */
  onConfirm,
  onCancel,

  casuistryActions
}) => {
  const { translate } = TranslationService;

  /* ===== Parte 1 ===== */
  const categories = json?.detectedCategories ?? [];
  const hasMany = categories.length > 1;
  const filesLabel =
    typeof json?.countFiles === "number" ? `${json.countFiles}` : undefined;

  const primaryCasuistryConfig = useMemo(() => {
    if (!casuistryActions || casuistryActions.length === 0) return null;
    const primaryCode = categories.reduce<number | null>((best, c) => {
      if (best === null) return c.code;
      const bestCat = categories.find((x) => x.code === best);
      return (c.confidence ?? 0) > (bestCat?.confidence ?? 0) ? c.code : best;
    }, null);
    return primaryCode !== null
      ? (casuistryActions.find((ca) => ca.code === primaryCode) ?? null)
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Parte 2 ===== */
  const [enabledSuggestion, setEnabledSuggestion] = useState(
    primaryCasuistryConfig ? primaryCasuistryConfig.actions.includes(1) : true
  );
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [includeInvoices, setIncludeInvoices] = useState(
    primaryCasuistryConfig ? primaryCasuistryConfig.actions.includes(4) : false
  );
  const [includePortalLink, setIncludePortalLink] = useState(
    primaryCasuistryConfig ? primaryCasuistryConfig.actions.includes(5) : false
  );
  const [emailAttachments, setEmailAttachments] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>("");
  const [hasSavedChanges, setHasSavedChanges] = useState(false);
  const editorRef = useRef<null | EditorType>(null);
  const [template, setTemplate] = useState<TemplateMeta | null>(null);
  const [originalEmailSubject, setOriginalEmailSubject] = useState<
    string | null
  >(null);
  const [originalEmailFrom, setOriginalEmailFrom] = useState<string | null>(
    null
  );
  const [editedSubject, setEditedSubject] = useState<string>("");
  const [editedSenderEmail, setEditedSenderEmail] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Cargar el asunto y el remitente del email de origen desde /activity/get
  useEffect(() => {
    const loadOriginalEmailData = async () => {
      if (!json?.emailId) {
        setOriginalEmailSubject(null);
        setOriginalEmailFrom(null);
        return;
      }

      try {
        const emailRequest = await ActivityService.get(json.emailId);
        if (emailRequest instanceof Error) {
          setOriginalEmailSubject(null);
          setOriginalEmailFrom(null);
          return;
        }

        setOriginalEmailSubject(emailRequest.item?.Subject || null);
        // Para email entrante, el From del original es el destinatario de la respuesta
        setOriginalEmailFrom(emailRequest.item?.From || null);
      } catch (error) {
        setOriginalEmailSubject(null);
        setOriginalEmailFrom(null);
      }
    };

    loadOriginalEmailData();
  }, [json?.emailId]);

  // Cargar la plantilla de forma asíncrona cuando hay mailTemplateId
  useEffect(() => {
    const loadTemplate = async () => {
      if (!json?.mailTemplateId || !resolveTemplate) {
        setTemplate(null);
        return;
      }

      try {
        const result = await resolveTemplate(json.mailTemplateId);
        setTemplate(result);
      } catch (error) {
        setTemplate(null);
      }
    };

    loadTemplate();
  }, [json?.mailTemplateId, resolveTemplate]);

  const suggestion = useMemo(
    () =>
      template
        ? {
            source: "template" as const,
            templateId: template.id,
            subject: template.subject ?? "",
            body: template.body ?? "",
            includesPendingList: !!template.includesPendingList
          }
        : {
            source: "ai" as const,
            templateId: null as string | null,
            subject: "",
            body: json?.responseMail ?? "",
            includesPendingList: false
          },
    [template, json?.responseMail]
  );

  // Inicializar los valores editables del asunto y remitente
  useEffect(() => {
    // Inicializar el asunto: usar el original si está disponible, sino el que viene en json o de la plantilla
    const templateSubject = template?.subject ?? "";
    const initialSubject =
      originalEmailSubject || json?.emailSubject || templateSubject || "";
    setEditedSubject(initialSubject);

    // Inicializar el remitente: usar el email del usuario actual
    const initialSenderEmail = CompanyService.getUserEmail() || "";
    setEditedSenderEmail(initialSenderEmail);
  }, [originalEmailSubject, json?.emailSubject, template?.subject]);

  // Estado del editor
  const getDefaultEditorState = useMemo(() => {
    return () => {
      const blocksFromHtml = htmlToDraft(suggestion.body ?? "");
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      return EditorState.createWithContent(contentState);
    };
  }, [suggestion.body]);

  const [editorState, setEditorState] = useState(() => getDefaultEditorState());
  const [editedBody, setEditedBody] = useState(suggestion.body);

  // Actualizar el editor cuando cambia la sugerencia
  // Solo actualizar si no hay cambios guardados y no está en modo edición
  useEffect(() => {
    if (!isEditingEmail && !hasSavedChanges) {
      const newState = getDefaultEditorState();
      setEditorState(newState);
      setEditedBody(suggestion.body);
    }
  }, [suggestion.body, isEditingEmail, hasSavedChanges, getDefaultEditorState]);

  // Configuración del toolbar del editor
  const toolbar: ToolbarOptions = {
    options: [
      "fontSize",
      "colorPicker",
      "inline",
      "list",
      "textAlign",
      "link",
      "remove"
    ],
    inline: {
      inDropdown: false,
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      options: ["bold", "italic", "underline"],
      bold: { className: undefined, title: undefined },
      italic: { className: undefined, title: undefined },
      underline: { className: undefined, title: undefined },
      strikethrough: { className: undefined, title: undefined },
      monospace: { className: undefined, title: undefined },
      superscript: { className: undefined, title: undefined },
      subscript: { className: undefined, title: undefined }
    },
    blockType: {
      inDropdown: true,
      options: [
        "Normal",
        "H1",
        "H2",
        "H3",
        "H4",
        "H5",
        "H6",
        "Blockquote",
        "Code"
      ],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      title: undefined
    },
    fontSize: {
      options: [8, 10, 12, 14, 18, 24, 36],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      title: undefined
    },
    fontFamily: {
      options: [
        "Arial",
        "Georgia",
        "Impact",
        "Tahoma",
        "Times New Roman",
        "Verdana"
      ],
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      title: undefined
    },
    list: {
      inDropdown: false,
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      options: ["unordered", "ordered"],
      unordered: { className: undefined, title: undefined },
      ordered: { className: undefined, title: undefined },
      indent: { className: undefined, title: undefined },
      outdent: { className: undefined, title: undefined },
      title: undefined
    },
    textAlign: {
      inDropdown: false,
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      options: ["left", "center", "right"],
      left: { className: undefined, title: undefined },
      center: { className: undefined, title: undefined },
      right: { className: undefined, title: undefined },
      justify: { className: undefined, title: undefined },
      title: undefined
    },
    colorPicker: {
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      colors: [
        "rgb(0 0 0)",
        "rgb(68,68,68)",
        "rgb(102,102,102)",
        "rgb(153,153,153)",
        "rgb(204,204,204)",
        "rgb(238,238,238)",
        "rgb(243,243,243)",
        "rgb(255,255,255)",
        "rgb(255,0,0)",
        "rgb(255,153,0)",
        "rgb(255,255,0)",
        "rgb(0,255,0)",
        "rgb(0,255,255)",
        "rgb(0,0,255)",
        "rgb(153,0,255)",
        "rgb(255,0,255)"
      ],
      title: undefined
    },
    link: {
      inDropdown: false,
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      dropdownClassName: undefined,
      showOpenOptionOnHover: true,
      defaultTargetOption: "_self",
      options: ["link", "unlink"],
      link: { className: undefined, title: undefined },
      unlink: { className: undefined, title: undefined },
      linkCallback: undefined
    },
    emoji: {
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      emojis: [],
      title: undefined
    },
    embedded: {
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      embedCallback: undefined,
      defaultSize: { height: "auto", width: "auto" },
      title: undefined
    },
    image: {
      className: undefined,
      component: undefined,
      popupClassName: undefined,
      urlEnabled: true,
      uploadEnabled: true,
      previewImage: false,
      alignmentEnabled: true,
      uploadCallback: undefined,
      inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
      alt: { present: false, mandatory: false },
      defaultSize: { height: "auto", width: "auto" },
      title: undefined
    },
    remove: {
      className: undefined,
      component: undefined,
      title: undefined
    },
    history: {
      inDropdown: false,
      className: undefined,
      component: undefined,
      dropdownClassName: undefined,
      options: ["undo", "redo"],
      undo: { className: undefined, title: undefined },
      redo: { className: undefined, title: undefined },
      title: undefined
    }
  };

  //const sourceBadge = suggestion.source === "template" ? `Plantilla Intiza #${suggestion.templateId}` : "Texto sugerido por IA";

  // Función para mostrar la preview del email
  const handleViewEmailPreview = async () => {
    if (!personId) {
      ToastService.showToast(
        translate.ErrorProcessingRequest ||
          "Error: No se pudo obtener el ID del cliente",
        undefined,
        "danger"
      );
      return;
    }

    setPreviewLoading(true);
    try {
      const emailContent =
        hasSavedChanges && editedBody
          ? editedBody
          : editedBody || suggestion.body;

      const previewRequest: SetEmailRequest = {
        item: {
          files: emailAttachments,
          draft: true,
          content: emailContent || "",
          personid: personId,
          from: editedSenderEmail || CompanyService.getUserEmail() || "",
          fromname: CompanyService.getUsers().find(
            (x) => x.Id === CompanyService.getUserid()
          )?.Value,
          to: originalEmailFrom || "",
          subject: editedSubject || suggestion.subject || "",
          copyme: false,
          html: true,
          includeinvoices: includeInvoices,
          listmode: includeInvoices ? "1" : null,
          quickfilter: includeInvoices ? EmailInvoiceListType.Pending : null,
          attach: false,
          includeattachment: false,
          IncludeAttachment: false,
          hasIO: false,
          replytoid: null,
          replacebody: "1",
          orderby0: undefined,
          orderby1: undefined,
          orderby2: undefined,
          orderbyorderdesc0: "off",
          orderbyorderdesc1: "off",
          orderbyorderdesc2: "off"
        }
      };

      const result = await ActivityService.previewEmailContent(previewRequest);

      if (result instanceof Error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        return;
      }

      // Mostrar la preview en el modal interno
      setPreviewContent(result.preview);
      setShowPreviewModal(true);
    } catch (error) {
      ToastService.showToast(
        translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  /* ===== Parte 3 ===== */
  const [enabledInvoiceUpdates, setEnabledInvoiceUpdates] = useState(
    primaryCasuistryConfig ? primaryCasuistryConfig.actions.includes(2) : true
  );
  const [editMode, setEditMode] = useState(false);
  const [showInvoiceStatusModal, setShowInvoiceStatusModal] = useState(false);
  const [isClosingInvoiceModal, setIsClosingInvoiceModal] = useState(false);
  const [selectedInvoiceRow, setSelectedInvoiceRow] =
    useState<InvoiceStatusRow | null>(null);
  const [invoiceStatusFormContent, setInvoiceStatusFormContent] =
    useState<React.ReactNode>(null);
  const [localInvoiceRows, setLocalInvoiceRows] = useState<InvoiceStatusRow[]>(
    json?.invoiceStatus ?? []
  );

  // Actualizar localInvoiceRows cuando cambia json.invoiceStatus
  useEffect(() => {
    setLocalInvoiceRows(json?.invoiceStatus ?? []);
  }, [json?.invoiceStatus]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const invoiceRows: InvoiceStatusRow[] = localInvoiceRows;

  // Mapa cacheado de definiciones adicionales de facturas para optimizar búsquedas
  const invoiceAdditionalDefinitionsMap = useMemo(() => {
    const definitions = CompanyService.getAdditionalDefinitions().filter(
      (ad) => ad.Entity === Entities.Invoice
    );
    const map = new Map<string, { name: string; type: number }>();
    definitions.forEach((def) => {
      map.set(def.AdditionalDefinitionID.toString(), {
        name: def.Name,
        type: def.Type
      });
    });
    return map;
  }, []);

  // Función para eliminar una fila de estado de factura
  const handleDeleteInvoiceRow = (invoice: string) => {
    const updatedRows = localInvoiceRows.filter((r) => r.invoice !== invoice);
    setLocalInvoiceRows(updatedRows);
  };

  // Función para abrir el modal de edición con el estado actual de la fila
  const handleEditInvoiceRow = (row: InvoiceStatusRow) => {
    if (!personId) {
      ToastService.showToast(
        translate.ErrorProcessingRequest ||
          "Error: No se pudo obtener el ID del cliente",
        undefined,
        "danger"
      );
      return;
    }

    const onStatusSubmit = async (statusEdit: InvoiceEditStatus) => {
      try {
        // Actualizar el estado local de las facturas
        const updatedRows = localInvoiceRows.map((r) =>
          r.invoice === row.invoice
            ? {
                ...r,
                statusId: statusEdit.statusId ?? r.statusId,
                additionalValues:
                  statusEdit.additionals?.map((a) => ({
                    valueId: a.Id.toString(),
                    valueLabel: a.Value
                  })) ?? r.additionalValues
              }
            : r
        );
        setLocalInvoiceRows(updatedRows);

        ToastService.showToast(
          translate.SavedChanges || "Cambios guardados",
          undefined,
          "success"
        );
        setShowInvoiceStatusModal(false);
        setIsClosingInvoiceModal(false);
        setTimeout(() => {
          setSelectedInvoiceRow(null);
          setInvoiceStatusFormContent(null);
        }, 300);
      } catch (error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      }
    };

    setSelectedInvoiceRow(row);

    // Obtener el estado actual de la fila
    const currentStatusId =
      typeof row.statusId === "number"
        ? row.statusId
        : row.statusId
          ? parseInt(String(row.statusId))
          : undefined;

    // Preparar los valores adicionales por defecto que vienen del servicio
    const defaultAdditionals =
      row.additionalValues?.map((av) => ({
        Id: av.valueId,
        Value: av.valueLabel
      })) ?? [];

    setInvoiceStatusFormContent(
      <InvoiceChangeStatusForm
        onSubmit={onStatusSubmit}
        personId={personId.toString()}
        defaultStatusId={currentStatusId}
        defaultAdditionals={defaultAdditionals}
      />
    );
    setShowInvoiceStatusModal(true);
  };

  // Función para manejar el cambio de estado de factura
  // Retorna una función que se ejecuta cuando se selecciona un estado (patrón usado en InvoiceTableItem)
  const handleInvoiceStatusChange = (
    row: InvoiceStatusRow,
    statusId: number
  ) => {
    if (!personId) {
      ToastService.showToast(
        translate.ErrorProcessingRequest ||
          "Error: No se pudo obtener el ID del cliente",
        undefined,
        "danger"
      );
      return () => {};
    }

    const onStatusSubmit = async (statusEdit: InvoiceEditStatus) => {
      try {
        // Actualizar el estado local de las facturas
        const updatedRows = localInvoiceRows.map((r) =>
          r.invoice === row.invoice
            ? {
                ...r,
                statusId: statusEdit.statusId ?? r.statusId,
                additionalValues:
                  statusEdit.additionals?.map((a) => ({
                    valueId: a.Id.toString(),
                    valueLabel: a.Value
                  })) ?? r.additionalValues
              }
            : r
        );
        setLocalInvoiceRows(updatedRows);

        ToastService.showToast(
          translate.SavedChanges || "Cambios guardados",
          undefined,
          "success"
        );
        setShowInvoiceStatusModal(false);
        setIsClosingInvoiceModal(false);
        setTimeout(() => {
          setSelectedInvoiceRow(null);
          setInvoiceStatusFormContent(null);
        }, 300);
      } catch (error) {
        ToastService.showToast(
          translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      }
    };

    // Siempre mostrar el modal cuando se selecciona un estado
    // El formulario se encargará de mostrar los campos adicionales si el estado los tiene
    return () => {
      setSelectedInvoiceRow(row);

      // Preparar los valores adicionales por defecto que vienen del servicio
      // Estos se precargarán en el formulario si son compatibles con el nuevo estado
      const defaultAdditionals =
        row.additionalValues?.map((av) => ({
          Id: av.valueId,
          Value: av.valueLabel
        })) ?? [];

      setInvoiceStatusFormContent(
        <InvoiceChangeStatusForm
          onSubmit={onStatusSubmit}
          personId={personId.toString()}
          defaultStatusId={statusId}
          defaultAdditionals={defaultAdditionals}
        />
      );
      setShowInvoiceStatusModal(true);
    };
  };

  /* ===== Parte 4 ===== */
  const [enabledTasks, setEnabledTasks] = useState(
    primaryCasuistryConfig ? primaryCasuistryConfig.actions.includes(3) : true
  );
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [isClosingTaskModal, setIsClosingTaskModal] = useState(false);
  const [taskFormContent, setTaskFormContent] = useState<React.ReactNode>(null);
  const [localTasks, setLocalTasks] = useState<Task[]>(json?.tasks ?? []);
  const [inlineEditResponsible, setInlineEditResponsible] =
    useState<string>("");
  const [inlineEditDate, setInlineEditDate] = useState<string>("");
  const [inlineEditDescription, setInlineEditDescription] =
    useState<string>("");

  // Actualizar localTasks cuando cambia json.tasks
  useEffect(() => {
    setLocalTasks(json?.tasks ?? []);
  }, [json?.tasks]);

  // Cargar casuistryActions cuando el padre no los provee, y pre-setear los switches
  useEffect(() => {
    if (casuistryActions !== undefined) return;
    UsecasesService.get().then((result) => {
      if (result instanceof Error || result.length === 0) return;
      const primaryCode = categories.reduce<number | null>((best, c) => {
        if (best === null) return c.code;
        const bestCat = categories.find((x) => x.code === best);
        return (c.confidence ?? 0) > (bestCat?.confidence ?? 0) ? c.code : best;
      }, null);
      const config =
        primaryCode !== null
          ? (result.find((ca) => ca.code === primaryCode) ?? null)
          : null;
      if (!config) return;
      setEnabledSuggestion(config.actions.includes(1));
      setEnabledInvoiceUpdates(config.actions.includes(2));
      setEnabledTasks(config.actions.includes(3));
      setIncludeInvoices(config.actions.includes(4));
      setIncludePortalLink(config.actions.includes(5));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tasks: Task[] = localTasks;

  // Función helper para prevenir scroll del body sin desplazar el contenido
  const preventBodyScroll = (isOpen: boolean) => {
    if (isOpen) {
      // Calcular el ancho del scrollbar antes de ocultarlo
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // Guardar los valores originales
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      // Ocultar el scrollbar y compensar con padding para evitar el desplazamiento
      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      return () => {
        // Restaurar los valores originales
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
    return () => {};
  };

  // Prevenir scroll del body cuando cualquier modal está abierto
  useEffect(() => {
    const isAnyModalOpen =
      showTaskModal ||
      isClosingTaskModal ||
      showInvoiceStatusModal ||
      isClosingInvoiceModal ||
      showPreviewModal ||
      isClosingModal;
    return preventBodyScroll(isAnyModalOpen);
  }, [
    showTaskModal,
    isClosingTaskModal,
    showInvoiceStatusModal,
    isClosingInvoiceModal,
    showPreviewModal,
    isClosingModal
  ]);

  /* ===== Construcción del objeto para confirmación ===== */
  const buildConfirmObject = (): AIResponse => {
    // Tomar el objeto completo original obtenido de aicategorize (con todas sus propiedades)
    const confirmObj: AIResponse = {
      ...json // Preservar todas las propiedades originales (tokens, cost, emailId, etc.)
    };

    // Asignar el asunto: usar el editado si existe, sino el original del email de origen
    confirmObj.emailSubject =
      editedSubject || originalEmailSubject || json?.emailSubject || null;

    // Asignar el remitente: usar el editado si existe, sino el email del usuario actual
    confirmObj.senderEmail =
      editedSenderEmail || CompanyService.getUserEmail() || null;

    // Aplicar solo las modificaciones del usuario basadas en los switches
    // Si la sugerencia está desactivada, limpiar campos relacionados
    if (!enabledSuggestion) {
      confirmObj.responseMail = null;
      confirmObj.mailTemplateId = null;
      confirmObj.emailAttachments = [];
      confirmObj.includeInvoices = false;
      // No limpiar emailSubject ya que es del email de origen
    } else {
      // Si se editó el email, usar el contenido editado (editedBody tiene prioridad si es diferente)
      if (editedBody && editedBody !== suggestion.body) {
        confirmObj.responseMail = editedBody;
      }

      // Guardar los archivos adjuntos si se agregaron
      confirmObj.emailAttachments =
        emailAttachments.length > 0 ? emailAttachments : [];

      // Guardar la configuración de incluir facturas
      confirmObj.includeInvoices = includeInvoices;
    }

    // Siempre incluir el estado del portal (switch visible independiente de la sugerencia)
    confirmObj.includePortalLink = includePortalLink;

    // Si las actualizaciones de facturas están desactivadas, limpiar el array
    if (!enabledInvoiceUpdates) {
      confirmObj.invoiceStatus = [];
    } else {
      // Usar las filas locales actualizadas
      confirmObj.invoiceStatus = localInvoiceRows;
    }

    // Si las tareas están desactivadas, limpiar el array
    if (!enabledTasks) {
      confirmObj.tasks = [];
    } else {
      // Usar las tareas locales actualizadas (que incluyen las ediciones)
      confirmObj.tasks = localTasks;
    }

    // El objeto resultante contiene todas las propiedades originales más las modificaciones del usuario
    return confirmObj;
  };

  const enableConfirm =
    enabledSuggestion ||
    (enabledInvoiceUpdates && invoiceRows.length > 0) ||
    (enabledTasks && tasks.length > 0);

  return (
    <div className="floatingPanelW" style={{ position: "relative" }}>
      <style>{`
                @keyframes fadeInSlide {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes slideInFromRight {
                    from {
                        transform: translateX(100%);
                    }
                    to {
                        transform: translateX(0);
                    }
                }
                
                @keyframes slideOutToRight {
                    from {
                        transform: translateX(0);
                    }
                    to {
                        transform: translateX(100%);
                    }
                }
                
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                
                @keyframes fadeOut {
                    from {
                        opacity: 1;
                    }
                    to {
                        opacity: 0;
                    }
                }
                
                .preview-modal-backdrop {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 9999 !important;
                    animation: fadeIn 0.3s ease-out;
                    pointer-events: auto;
                }
                
                .preview-modal-backdrop.closing {
                    animation: fadeOut 0.3s ease-out;
                }
                
                .preview-modal-container {
                    position: fixed !important;
                    top: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    width: 80%;
                    max-width: 1200px;
                    background: #fff;
                    z-index: 10000 !important;
                    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
                    display: flex;
                    flex-direction: column;
                    animation: slideInFromRight 0.3s ease-out;
                    pointer-events: auto;
                }
                
                .preview-modal-container.closing {
                    animation: slideOutToRight 0.3s ease-out;
                }
                
                .preview-modal-header {
                    padding: 2px 20px;
                    border-bottom: 1px solid #e6e8eb;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background-color: rgb(31, 43, 69);
                    min-height: 40px;
                }
                
                .preview-modal-title {
                    font-size: 13px;
                    font-weight: 600;
                    color: white;
                
                    margin: 0;
                }
                
                .preview-modal-close {
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #475467;
                    font-size: 20px;
                    border-radius: 4px;
                    transition: background 0.2s ease;
                }
                
                .preview-modal-close:hover {
                    background: #f1f3f5;
                }
                
                .preview-modal-content {
                    flex: 1;
                    overflow: auto;
                    padding: 20px;
                }
            `}</style>
      <div className="floatingBodyW px-2 py-1">
        <div style={{ fontFamily: "Inter, system-ui, Arial, sans-serif" }}>
          {/* ===== Parte 1: Análisis ===== */}
          <div style={{ ...box, marginTop: 10 }}>
            <h6 style={title}>{translate.MessageAnalysis}</h6>

            <div
              style={{
                background: "#87d1c72b", // celeste suave
                borderRadius: 14,
                padding: 14,
                paddingBottom: 18,
                marginTop: 10,
                marginBottom: 10
              }}
            >
              {hasMany && (
                <div
                  style={{
                    marginTop: 2,
                    marginBottom: 10,
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "#fff4e6",
                    color: "#b54708",
                    fontSize: 13,
                    fontWeight: 500,
                    border: "1px solid #fed7aa"
                  }}
                >
                  {translate.MultipleCasuisticsDetected}
                </div>
              )}

              {/* Casuísticas */}
              {categories.length === 0 ? (
                <div style={{ marginTop: 0 }}>
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: "#475467" }}
                  >
                    {translate.Casuisics}:{" "}
                  </span>
                  <Empty text={translate.NoCasuisticsDetected} />
                </div>
              ) : (
                categories.map((c, i) => (
                  <CategoryCard key={i} cat={c} idx={i} />
                ))
              )}

              {/* Archivos detectados */}
              <div style={{ marginTop: 16, lineHeight: 1.6 }}>
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: "#475467" }}
                >
                  {translate.DetectedFiles}:{" "}
                </span>
                {filesLabel ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "2px 8px",
                      marginLeft: 4,
                      marginRight: 4,
                      borderRadius: 999,
                      background: "#e0e7ff",
                      color: "#4338ca",
                      fontSize: 12,
                      fontWeight: 600,
                      verticalAlign: "middle"
                    }}
                  >
                    {filesLabel}
                  </span>
                ) : (
                  <span style={{ fontSize: 13, color: "#101828" }}>
                    <Empty text={translate.NoFilesDetected} />
                  </span>
                )}
              </div>

              {/* Acción recomendada */}
              <div style={{ marginTop: 16, lineHeight: 1.6 }}>
                <span
                  style={{ fontSize: 13, fontWeight: 700, color: "#475467" }}
                >
                  {translate.RecommendedAction}:{" "}
                </span>
                <span style={{ fontSize: 13, color: "#101828" }}>
                  {json?.action ? (
                    json.action
                  ) : (
                    <Empty text={translate.NoActionSuggested} />
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* ===== Parte 2: Sugerencia de respuesta ===== */}
          <div style={{ ...box, marginTop: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12
              }}
            >
              <h6 style={title}>{translate.ClientResponseSuggestion}</h6>
              <label style={switchWrap} title={translate.ToggleAction}>
                <input
                  type="checkbox"
                  checked={enabledSuggestion}
                  onChange={(e) => {
                    setEnabledSuggestion(e.target.checked);
                    // Si se desactiva, resetear los cambios guardados
                    if (!e.target.checked) {
                      setHasSavedChanges(false);
                    }
                  }}
                  style={{ display: "none" }}
                />
                <span
                  aria-hidden
                  style={{
                    width: 44,
                    height: 24,
                    background: enabledSuggestion ? "#0076b6" : "#e5e7eb",
                    borderRadius: 999,
                    position: "relative",
                    transition: "background 120ms ease"
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 3,
                      left: enabledSuggestion ? 23 : 3,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      transition: "left 120ms ease"
                    }}
                  />
                </span>
              </label>
            </div>

            {/* Contenedor con elevación */}
            {enabledSuggestion && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e6e8eb",
                  padding: 16,
                  boxShadow:
                    "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.06)",
                  animation: "fadeInSlide 0.3s ease-out",
                  transition: "opacity 0.3s ease-out, transform 0.3s ease-out"
                }}
              >
                {!isEditingEmail ? (
                  <>
                    {/* Remitente (vista de solo lectura) */}
                    <div style={{ marginBottom: 12 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#475467",
                          display: "block",
                          marginBottom: 4
                        }}
                      >
                        {translate.Sender || "Remitente"}:
                      </span>
                      <span style={{ fontSize: 13, color: "#101828" }}>
                        {editedSenderEmail || CompanyService.getUserEmail() || (
                          <Empty text={translate.NoSender || "Sin remitente"} />
                        )}
                      </span>
                    </div>

                    {/* Asunto (vista de solo lectura) */}
                    <div style={{ marginBottom: 12 }}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#475467",
                          display: "block",
                          marginBottom: 4
                        }}
                      >
                        {translate.Subject || "Asunto"}:
                      </span>
                      <span style={{ fontSize: 13, color: "#101828" }}>
                        {editedSubject ||
                          originalEmailSubject ||
                          json?.emailSubject ||
                          suggestion.subject || (
                            <Empty text={translate.NoSubject || "Sin asunto"} />
                          )}
                      </span>
                    </div>

                    {/* Texto del correo (vista previa) */}
                    <SafeHtml
                      html={
                        editedBody || suggestion.body
                          ? editedBody || suggestion.body
                          : `<span style="color: #98A2B3">${translate.NoSuggestedContent}</span>`
                      }
                      style={{
                        borderRadius: 8,
                        padding: 14,
                        background: "#fafafa",
                        minHeight: 120,
                        color: "#101828",
                        fontSize: 13,
                        lineHeight: 1.6,
                        height: "100%"
                      }}
                    />

                    {/* Footer con info y botones */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 12
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          alignItems: "flex-start"
                        }}
                      >
                        {(includeInvoices ||
                          suggestion.includesPendingList) && (
                          <div
                            style={{
                              fontSize: 12,
                              fontStyle: "italic",
                              color: "#98A2B3"
                            }}
                          >
                            {translate.IncludesPendingInvoices ||
                              "Incluye listado de facturas pendientes"}
                          </div>
                        )}
                        {hasSavedChanges &&
                          editedBody &&
                          editedBody !== suggestion.body && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#059669",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: 4
                              }}
                            >
                              <i
                                className="fas fa-check-circle"
                                style={{ fontSize: 12 }}
                              />
                              <span>
                                {translate.ChangesSaved || "Cambios guardados"}
                              </span>
                            </div>
                          )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center"
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: previewLoading ? "wait" : "pointer",
                            padding: 4,
                            display: "flex",
                            alignItems: "center",
                            color: "#475467",
                            opacity: previewLoading ? 0.6 : 1
                          }}
                          onClick={handleViewEmailPreview}
                          disabled={previewLoading}
                          title={translate.ViewEmail || "Vista previa"}
                        >
                          {previewLoading ? (
                            <i
                              className="fas fa-spinner-third fa-spin"
                              style={{ fontSize: 14 }}
                            />
                          ) : (
                            <EyeIcon />
                          )}
                        </button>
                        <button
                          type="button"
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: 4,
                            display: "flex",
                            alignItems: "center",
                            color: isEditingEmail ? "#98A2B3" : "#475467"
                          }}
                          onClick={() => {
                            if (isEditingEmail) {
                              // Cancelar edición: restaurar el contenido original
                              const newState = getDefaultEditorState();
                              setEditorState(newState);
                              setEditedBody(suggestion.body);
                              // Restaurar asunto y remitente a los valores originales
                              const templateSubject = template?.subject ?? "";
                              setEditedSubject(
                                originalEmailSubject ||
                                  json?.emailSubject ||
                                  templateSubject ||
                                  ""
                              );
                              setEditedSenderEmail(
                                CompanyService.getUserEmail() || ""
                              );
                              setHasSavedChanges(false);
                              setIsEditingEmail(false);
                            } else {
                              // Al volver a editar, cargar el contenido editado en el editor si existe
                              if (hasSavedChanges && editedBody) {
                                const blocksFromHtml = htmlToDraft(editedBody);
                                const { contentBlocks, entityMap } =
                                  blocksFromHtml;
                                const contentState =
                                  ContentState.createFromBlockArray(
                                    contentBlocks,
                                    entityMap
                                  );
                                setEditorState(
                                  EditorState.createWithContent(contentState)
                                );
                              }
                              setIsEditingEmail(true);
                            }
                          }}
                          title={
                            isEditingEmail
                              ? translate.Cancel || "Cancelar"
                              : translate.EditEmail
                          }
                        >
                          {isEditingEmail ? <CloseIcon /> : <PencilIcon />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Campo de Remitente */}
                    <div style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#475467"
                        }}
                      >
                        {translate.Sender || "Remitente"}
                      </label>
                      <input
                        type="email"
                        value={editedSenderEmail}
                        onChange={(e) => setEditedSenderEmail(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d0d5dd",
                          borderRadius: 8,
                          fontSize: 14,
                          color: "#101828",
                          outline: "none",
                          transition: "border-color 0.2s ease"
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#0076b6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#d0d5dd")}
                        placeholder={
                          translate.SenderEmail || "Email del remitente"
                        }
                      />
                    </div>

                    {/* Campo de Asunto */}
                    <div style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#475467"
                        }}
                      >
                        {translate.Subject || "Asunto"}
                      </label>
                      <input
                        type="text"
                        value={editedSubject}
                        onChange={(e) => setEditedSubject(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d0d5dd",
                          borderRadius: 8,
                          fontSize: 14,
                          color: "#101828",
                          outline: "none",
                          transition: "border-color 0.2s ease"
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#0076b6")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#d0d5dd")}
                        placeholder={
                          translate.EmailSubject || "Asunto del email"
                        }
                      />
                    </div>

                    {/* Editor de email */}
                    <div style={{ marginBottom: 16 }}>
                      <EmailEditor
                        toolbar={toolbar}
                        editorState={editorState}
                        onEditorStateChange={(newEditorState) => {
                          setEditorState(newEditorState);
                          const rawContentState = convertToRaw(
                            newEditorState.getCurrentContent()
                          );
                          const markup = draftToHtml(
                            rawContentState,
                            {},
                            false
                          );
                          setEditedBody(markup);
                        }}
                        locale={TranslationService.currentLanguage}
                        ref={(x) => (editorRef.current = Cast<EditorType>(x))}
                      />
                    </div>

                    {/* Switch de incluir facturas */}
                    <div style={{ marginBottom: 16 }}>
                      <SwitchButton
                        label={translate.IncludeInvoices}
                        onChange={setIncludeInvoices}
                        defaultValue={includeInvoices}
                      />
                    </div>

                    {/* Switch de incluir vínculo al portal del cliente */}
                    <div style={{ marginBottom: 16 }}>
                      <SwitchButton
                        label={translate.IncludePortalLink}
                        onChange={setIncludePortalLink}
                        defaultValue={includePortalLink}
                      />
                    </div>

                    {/* Componente para subir adjuntos */}
                    <div style={{ marginBottom: 16 }}>
                      <MultiFileUploadNew
                        files={emailAttachments}
                        onFilesChange={(files) =>
                          setEmailAttachments(
                            files.map((f) => ({ id: f.id, name: f.name }))
                          )
                        }
                      />
                    </div>

                    {/* Botones de acción */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 12
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          // Restaurar el contenido original
                          const newState = getDefaultEditorState();
                          setEditorState(newState);
                          setEditedBody(suggestion.body);
                          // Restaurar asunto y remitente a los valores originales
                          setEditedSubject(
                            originalEmailSubject ||
                              json?.emailSubject ||
                              suggestion.subject ||
                              ""
                          );
                          setEditedSenderEmail(
                            CompanyService.getUserEmail() || ""
                          );
                          setHasSavedChanges(false);
                          setIsEditingEmail(false);
                        }}
                        style={{
                          border: "1px solid #d0d5dd",
                          background: "#fff",
                          borderRadius: 8,
                          padding: "8px 16px",
                          cursor: "pointer",
                          fontSize: 14,
                          color: "#475467"
                        }}
                      >
                        {translate.Cancel || "Cancelar"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Guardar los cambios y salir del modo edición
                          // Marcar que hay cambios guardados para que se muestren en la vista previa
                          setHasSavedChanges(true);
                          setIsEditingEmail(false);
                        }}
                        style={{
                          border: "none",
                          background: "#0076b6",
                          borderRadius: 8,
                          padding: "8px 16px",
                          cursor: "pointer",
                          fontSize: 14,
                          color: "#fff",
                          fontWeight: 600
                        }}
                      >
                        {translate.Save || "Guardar"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* ===== Parte 3: Estados de facturas ===== */}
          <div style={{ ...box, marginTop: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12
              }}
            >
              <h6 style={title}>{translate.AIInvoiceStatusUpdate}</h6>
              <label style={switchWrap} title={translate.ToggleAction}>
                <input
                  type="checkbox"
                  checked={enabledInvoiceUpdates}
                  onChange={(e) => setEnabledInvoiceUpdates(e.target.checked)}
                  style={{ display: "none" }}
                />
                <span
                  aria-hidden
                  style={{
                    width: 44,
                    height: 24,
                    background: enabledInvoiceUpdates ? "#0076b6" : "#e5e7eb",
                    borderRadius: 999,
                    position: "relative",
                    transition: "background 120ms ease"
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 3,
                      left: enabledInvoiceUpdates ? 23 : 3,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      transition: "left 120ms ease"
                    }}
                  />
                </span>
              </label>
            </div>
            {enabledInvoiceUpdates && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e6e8eb",
                  padding: 16,
                  boxShadow:
                    "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.06)",
                  animation: "fadeInSlide 0.3s ease-out",
                  transition: "opacity 0.3s ease-out, transform 0.3s ease-out"
                }}
              >
                <div style={{ ...row }}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: "30%" }}>
                          {translate.AIInvoiceNumber}
                        </th>
                        <th style={{ ...th, width: "auto" }}>
                          {translate.AIInvoiceStatusLabel}
                        </th>
                        <th style={{ ...th, width: "54px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoiceRows.length ? invoiceRows : []).map((r, i) => {
                        // Obtener el label del estado usando CompanyService
                        const statusId =
                          typeof r.statusId === "number"
                            ? r.statusId
                            : parseInt(String(r.statusId));
                        const status = CompanyService.getIoStatus().find(
                          (x) => x.IOStatusID === statusId
                        );
                        const statusName =
                          status?.Value ??
                          CompanyService.getNormalIOStatusName() ??
                          String(r.statusId);

                        // Crear las opciones del Select con funciones como valores (patrón de InvoiceTableItem)
                        const allowedStatuses =
                          CompanyService.getAllowedInvoiceStatuses();
                        let statuses = allowedStatuses.map((x) => ({
                          text: x.Value,
                          value: handleInvoiceStatusChange(r, x.IOStatusID)
                        }));

                        // Si el estado actual no está en los permitidos, agregarlo para poder mostrarlo
                        const currentStatusInAllowed = allowedStatuses.find(
                          (s) => s.IOStatusID === statusId
                        );
                        if (!currentStatusInAllowed && status) {
                          statuses.unshift({
                            text: status.Value,
                            value: handleInvoiceStatusChange(r, statusId)
                          });
                        }

                        // Encontrar la función de acción correspondiente al estado actual
                        const currentStatusAction = statuses.find((x) => {
                          const currentStatusId =
                            typeof r.statusId === "number"
                              ? r.statusId
                              : parseInt(String(r.statusId));
                          const statusOption =
                            CompanyService.getIoStatus().find(
                              (s) => s.IOStatusID === currentStatusId
                            );
                          return (
                            x.text ===
                            (statusOption?.Value ??
                              CompanyService.getNormalIOStatusName())
                          );
                        })?.value;

                        // Función para manejar el cambio y mover el foco al siguiente elemento (tab behavior)
                        const handleSelectChange = (actionFn: () => void) => {
                          actionFn();
                          // Mover foco al siguiente select o botón de eliminar
                          setTimeout(() => {
                            // Buscar la fila actual por el número de factura
                            const allRows = Array.from(
                              document.querySelectorAll("tbody tr")
                            );
                            const currentRowIndex = allRows.findIndex((row) => {
                              const firstCell =
                                row.querySelector("td:first-child");
                              return (
                                firstCell?.textContent?.trim() === r.invoice
                              );
                            });

                            if (
                              currentRowIndex >= 0 &&
                              currentRowIndex < allRows.length - 1
                            ) {
                              const nextRow = allRows[currentRowIndex + 1];
                              const nextSelect =
                                nextRow.querySelector("select");
                              if (nextSelect) {
                                nextSelect.focus();
                              } else {
                                const nextButton =
                                  nextRow.querySelector("button");
                                if (nextButton) {
                                  nextButton.focus();
                                }
                              }
                            }
                          }, 100);
                        };

                        // Crear el contenido del tooltip con la información de la factura
                        const formatCurrencyValue = (
                          value?: number,
                          currencyId?: number
                        ) => {
                          if (value === undefined || value === null) return "—";
                          const currency = currencyId
                            ? CompanyService.getCurrencies().find(
                                (c) => c.CurrencyId === currencyId
                              )
                            : null;
                          const currencySymbol = currency?.Symbol || "";
                          return `${currencySymbol} ${value.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        };

                        const formatDateValue = (dateStr?: string) => {
                          if (!dateStr) return "—";
                          try {
                            return new Date(dateStr).toLocaleDateString(
                              "es-AR",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit"
                              }
                            );
                          } catch {
                            return dateStr;
                          }
                        };

                        const tooltipContent = (
                          <div
                            style={{
                              padding: "8px",
                              fontSize: "12px",
                              lineHeight: "1.6",
                              maxWidth: "350px"
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                marginBottom: "10px",
                                color: "#101828",
                                fontSize: "13px"
                              }}
                            >
                              {translate.AIInvoiceNumber || "Factura"}:{" "}
                              {r.invoice}
                            </div>

                            {/* Información básica de la factura */}
                            <div style={{ marginBottom: "6px" }}>
                              <span
                                style={{ fontWeight: 600, color: "#475467" }}
                              >
                                {translate.AIInvoiceStatusLabel || "Estado"}:
                              </span>{" "}
                              <span style={{ color: "#101828" }}>
                                {statusName}
                              </span>
                            </div>

                            {r.amount !== undefined && (
                              <div style={{ marginBottom: "6px" }}>
                                <span
                                  style={{ fontWeight: 600, color: "#475467" }}
                                >
                                  {translate.Amount || "Monto"}:
                                </span>
                                <span
                                  style={{
                                    color: "#101828",
                                    marginLeft: "4px"
                                  }}
                                >
                                  {formatCurrencyValue(r.amount, r.currencyId)}
                                </span>
                              </div>
                            )}

                            {r.pending !== undefined && r.pending !== null && (
                              <div style={{ marginBottom: "6px" }}>
                                <span
                                  style={{ fontWeight: 600, color: "#475467" }}
                                >
                                  {translate.Pending || "Pendiente"}:
                                </span>
                                <span
                                  style={{
                                    color: "#101828",
                                    marginLeft: "4px"
                                  }}
                                >
                                  {formatCurrencyValue(r.pending, r.currencyId)}
                                </span>
                              </div>
                            )}

                            {r.emittedDate && (
                              <div style={{ marginBottom: "6px" }}>
                                <span
                                  style={{ fontWeight: 600, color: "#475467" }}
                                >
                                  {translate.IssueDate || "Fecha de emisión"}:
                                </span>
                                <span
                                  style={{
                                    color: "#101828",
                                    marginLeft: "4px"
                                  }}
                                >
                                  {formatDateValue(r.emittedDate)}
                                </span>
                              </div>
                            )}

                            {r.dueDate && (
                              <div style={{ marginBottom: "6px" }}>
                                <span
                                  style={{ fontWeight: 600, color: "#475467" }}
                                >
                                  {translate.DueDate || "Fecha de vencimiento"}:
                                </span>
                                <span
                                  style={{
                                    color: "#101828",
                                    marginLeft: "4px"
                                  }}
                                >
                                  {formatDateValue(r.dueDate)}
                                </span>
                              </div>
                            )}

                            {r.description && (
                              <div style={{ marginBottom: "6px" }}>
                                <span
                                  style={{ fontWeight: 600, color: "#475467" }}
                                >
                                  {translate.Observations || "Descripción"}:
                                </span>
                                <div
                                  style={{
                                    color: "#101828",
                                    marginLeft: "4px",
                                    marginTop: "2px",
                                    wordBreak: "break-word"
                                  }}
                                >
                                  {r.description}
                                </div>
                              </div>
                            )}

                            {/* Campos adicionales */}
                            {r.additionalValues &&
                              r.additionalValues.length > 0 && (
                                <div
                                  style={{
                                    marginTop: "8px",
                                    paddingTop: "8px",
                                    borderTop: "1px solid #e6e8eb"
                                  }}
                                >
                                  {r.additionalValues.map((av, idx) => {
                                    // Obtener el nombre y tipo del campo adicional desde el mapa cacheado
                                    // Convertir valueId a string para buscar en el mapa (puede venir como número o string)
                                    const valueIdStr = String(av.valueId);
                                    const fieldDef =
                                      invoiceAdditionalDefinitionsMap.get(
                                        valueIdStr
                                      );
                                    const fieldName =
                                      fieldDef?.name || av.valueId;

                                    // Formatear el valor según el tipo de dato
                                    let displayValue = av.valueLabel || "—";
                                    if (
                                      fieldDef?.type === DataType.Date &&
                                      av.valueLabel
                                    ) {
                                      const parsedDate = parseDate(
                                        av.valueLabel
                                      );
                                      if (parsedDate) {
                                        displayValue =
                                          formatDateDigits(parsedDate);
                                      }
                                    }

                                    return (
                                      <div
                                        key={idx}
                                        style={{
                                          marginLeft: "8px",
                                          marginBottom: "4px",
                                          color: "#101828"
                                        }}
                                      >
                                        <span style={{ fontWeight: 500 }}>
                                          {fieldName}:{" "}
                                        </span>
                                        {displayValue}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        );

                        return (
                          <TooltipComponent
                            title={tooltipContent}
                            placement="top"
                          >
                            <tr key={`${r.invoice}-${i}`}>
                              <td style={td}>{r.invoice || <Empty />}</td>
                              <td
                                style={td}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {editMode ? (
                                  <Select
                                    options={statuses}
                                    onChange={handleSelectChange}
                                    extraClass="invoice-table-select"
                                    value={currentStatusAction}
                                    defaultValue={currentStatusAction}
                                  />
                                ) : (
                                  <span>{statusName}</span>
                                )}
                              </td>
                              <td style={{ ...td, textAlign: "right" }}>
                                {editMode && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      gap: 8
                                    }}
                                  >
                                    <button
                                      type="button"
                                      style={{
                                        border: "none",
                                        outline: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        padding: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#475467"
                                      }}
                                      onClick={() => handleEditInvoiceRow(r)}
                                      title={translate.Edit || "Editar"}
                                    >
                                      <PencilIcon />
                                    </button>
                                    <button
                                      type="button"
                                      style={{
                                        border: "none",
                                        outline: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        padding: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        color: "#dc3545"
                                      }}
                                      onClick={() =>
                                        handleDeleteInvoiceRow(r.invoice)
                                      }
                                      title={translate.Delete || "Eliminar"}
                                    >
                                      <TrashIcon />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          </TooltipComponent>
                        );
                      })}
                      {invoiceRows.length === 0 && (
                        <tr>
                          <td style={td} colSpan={3}>
                            <Empty text={translate.AINoInvoicesToUpdate} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer con botones */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginTop: 12,
                    gap: 8
                  }}
                >
                  <button
                    type="button"
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 4,
                      display: "flex",
                      alignItems: "center",
                      color: editMode ? "#98A2B3" : "#475467"
                    }}
                    onClick={() => setEditMode((v) => !v)}
                    title={
                      editMode
                        ? translate.Cancel || "Cancelar"
                        : translate.AIEnableStatusEditing
                    }
                  >
                    {editMode ? <CloseIcon /> : <PencilIcon />}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ===== Parte 4: Creación de tareas programadas ===== */}
          <div style={{ ...box, marginTop: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 12
              }}
            >
              <h6 style={title}>{translate.AIScheduledTaskCreation}</h6>
              <label style={switchWrap} title={translate.ToggleAction}>
                <input
                  type="checkbox"
                  checked={enabledTasks}
                  onChange={(e) => setEnabledTasks(e.target.checked)}
                  style={{ display: "none" }}
                />
                <span
                  aria-hidden
                  style={{
                    width: 44,
                    height: 24,
                    background: enabledTasks ? "#0076b6" : "#e5e7eb",
                    borderRadius: 999,
                    position: "relative",
                    transition: "background 120ms ease"
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 3,
                      left: enabledTasks ? 23 : 3,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      transition: "left 120ms ease"
                    }}
                  />
                </span>
              </label>
            </div>

            {enabledTasks && (
              <div style={{ ...row }}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 8,
                    border: "1px solid #e6e8eb",
                    padding: 16,
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.06)",
                    animation: "fadeInSlide 0.3s ease-out",
                    transition: "opacity 0.3s ease-out, transform 0.3s ease-out"
                  }}
                >
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={{ ...th, width: "28%" }}>
                          {translate.AIResponsible}
                        </th>
                        <th style={{ ...th, width: "20%" }}>
                          {translate.AIExpirationDate}
                        </th>
                        <th style={th}>{translate.AITaskDescription}</th>
                        <th style={{ ...th, width: "54px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tasks.length ? tasks : []).map((t, i) => {
                        const responsibleId = String(
                          t.responsible || ""
                        ).toLowerCase();
                        const resolved =
                          resolveUserName?.(t.responsible) ||
                          CompanyService.getUsers()?.find(
                            (u) =>
                              String(u.Id) === responsibleId ||
                              u.Value?.toLowerCase() === responsibleId
                          )?.Value ||
                          t.responsible ||
                          "";
                        const prettyDate = formatDate(t.expirationDate);
                        const text = t.description || "";
                        const short = truncate(text, 90);
                        const isEditing = editingTaskIndex === i;

                        const handleEditTask = () => {
                          if (isEditing) {
                            // Cancelar edición inline
                            setEditingTaskIndex(null);
                            setInlineEditResponsible("");
                            setInlineEditDate("");
                            setInlineEditDescription("");
                          } else {
                            // Iniciar edición inline con valores actuales
                            const responsibleStr = String(
                              t.responsible || ""
                            ).toLowerCase();
                            const matchedUser = CompanyService.getUsers()?.find(
                              (u) =>
                                String(u.Id) === responsibleStr ||
                                u.Value?.toLowerCase() === responsibleStr
                            );
                            setInlineEditResponsible(
                              matchedUser?.Id || t.responsible || ""
                            );
                            setInlineEditDate(
                              t.expirationDate
                                ? t.expirationDate.substring(0, 10)
                                : ""
                            );
                            setInlineEditDescription(t.description || "");
                            setEditingTaskIndex(i);
                          }
                        };

                        return (
                          <React.Fragment key={`task-${i}`}>
                            <tr>
                              <td style={td}>{resolved || <Empty />}</td>
                              <td style={td}>{prettyDate || <Empty />}</td>
                              <td style={{ ...td }} title={text}>
                                {short || <Empty />}
                              </td>
                              <td style={{ ...td, textAlign: "right" }}>
                                <button
                                  type="button"
                                  style={{
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    padding: 4,
                                    display: "flex",
                                    alignItems: "center",
                                    color: isEditing ? "#98A2B3" : "#475467"
                                  }}
                                  onClick={handleEditTask}
                                  title={
                                    isEditing
                                      ? translate.Cancel || "Cancelar"
                                      : translate.AIEditTaskModal
                                  }
                                >
                                  {isEditing ? <CloseIcon /> : <PencilIcon />}
                                </button>
                              </td>
                            </tr>
                            {isEditing && (
                              <tr>
                                <td
                                  colSpan={4}
                                  style={{
                                    padding: "12px 16px",
                                    background: "#f8f9fa",
                                    borderBottom: "1px solid #f1f3f5"
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 12
                                    }}
                                  >
                                    <div>
                                      <label
                                        style={{
                                          display: "block",
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#475467",
                                          marginBottom: 4
                                        }}
                                      >
                                        {translate.AIResponsible}
                                      </label>
                                      <select
                                        value={inlineEditResponsible}
                                        onChange={(e) =>
                                          setInlineEditResponsible(
                                            e.target.value
                                          )
                                        }
                                        style={{
                                          width: "100%",
                                          padding: "8px 12px",
                                          border: "1px solid #d0d5dd",
                                          borderRadius: 8,
                                          fontSize: 13,
                                          color: "#101828"
                                        }}
                                      >
                                        <option value="">—</option>
                                        {CompanyService.getUsers()?.map((u) => (
                                          <option key={u.Id} value={u.Id}>
                                            {u.Value}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label
                                        style={{
                                          display: "block",
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#475467",
                                          marginBottom: 4
                                        }}
                                      >
                                        {translate.AIExpirationDate}
                                      </label>
                                      <input
                                        type="date"
                                        value={inlineEditDate}
                                        onChange={(e) =>
                                          setInlineEditDate(e.target.value)
                                        }
                                        style={{
                                          width: "100%",
                                          padding: "8px 12px",
                                          border: "1px solid #d0d5dd",
                                          borderRadius: 8,
                                          fontSize: 13,
                                          color: "#101828"
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <label
                                        style={{
                                          display: "block",
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#475467",
                                          marginBottom: 4
                                        }}
                                      >
                                        {translate.AITaskDescription}
                                      </label>
                                      <textarea
                                        value={inlineEditDescription}
                                        onChange={(e) =>
                                          setInlineEditDescription(
                                            e.target.value
                                          )
                                        }
                                        rows={3}
                                        style={{
                                          width: "100%",
                                          padding: "8px 12px",
                                          border: "1px solid #d0d5dd",
                                          borderRadius: 8,
                                          fontSize: 13,
                                          color: "#101828",
                                          resize: "vertical"
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: 8
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingTaskIndex(null);
                                          setInlineEditResponsible("");
                                          setInlineEditDate("");
                                          setInlineEditDescription("");
                                        }}
                                        style={{
                                          border: "1px solid #d0d5dd",
                                          background: "#fff",
                                          borderRadius: 8,
                                          padding: "8px 16px",
                                          cursor: "pointer",
                                          fontSize: 14,
                                          color: "#475467"
                                        }}
                                      >
                                        {translate.Cancel || "Cancelar"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedTask: Task = {
                                            ...t,
                                            responsible: inlineEditResponsible,
                                            expirationDate:
                                              inlineEditDate || undefined,
                                            description: inlineEditDescription
                                          };
                                          setLocalTasks(
                                            localTasks.map((task, idx) =>
                                              idx === i ? updatedTask : task
                                            )
                                          );
                                          setEditingTaskIndex(null);
                                          setInlineEditResponsible("");
                                          setInlineEditDate("");
                                          setInlineEditDescription("");
                                          onEditTask?.(updatedTask);
                                        }}
                                        style={{
                                          border: "none",
                                          background: "#0076b6",
                                          borderRadius: 8,
                                          padding: "8px 16px",
                                          cursor: "pointer",
                                          fontSize: 14,
                                          color: "#fff",
                                          fontWeight: 600
                                        }}
                                      >
                                        {translate.Save || "Guardar"}
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {tasks.length === 0 && (
                        <tr>
                          <td style={td} colSpan={4}>
                            <Empty text={translate.AINoTasksSuggested} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer con botones Cancelar / Confirmar - fuera del área scrolleable */}
      {onConfirm && (
        <div
          className="floatingFooterW"
          style={{ justifyContent: "space-between" }}
        >
          <span style={{ fontSize: 12, color: "#7a8694" }}>
            {translate.AIAgentConfirmHint ||
              "Al confirmar, el Agente IA ejecuta las acciones activadas."}
          </span>
          <div style={{ display: "flex", gap: 10 }}>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                style={{
                  background: "#fff",
                  color: "#344054",
                  border: "1px solid #e6e8ec",
                  borderRadius: 8,
                  padding: "10px 22px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                {translate.Cancel}
              </button>
            )}
            <button
              type="button"
              onClick={async () => {
                if (onConfirm) {
                  const confirmObject = buildConfirmObject();
                  setIsSaving(true);
                  await onConfirm(confirmObject);
                  setIsSaving(false);
                }
              }}
              style={{
                background: enableConfirm ? "#0076b6" : "#e5e7eb",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: enableConfirm ? "pointer" : "default",
                transition: "background 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = enableConfirm
                  ? "#005a8f"
                  : "#e5e7eb";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = enableConfirm
                  ? "#0076b6"
                  : "#e5e7eb";
              }}
              disabled={isSaving || !enableConfirm}
            >
              {translate.Confirm || "Confirmar"}
              {isSaving && <i className="fas fa-spinner-third fa-spin"></i>}
            </button>
          </div>
        </div>
      )}

      {/* Modal de Preview de Facturas */}
      {(showInvoiceStatusModal || isClosingInvoiceModal) && (
        <>
          <div
            className={`preview-modal-backdrop ${isClosingInvoiceModal ? "closing" : ""}`}
            onClick={() => {
              setIsClosingInvoiceModal(true);
              setTimeout(() => {
                setShowInvoiceStatusModal(false);
                setIsClosingInvoiceModal(false);
                setSelectedInvoiceRow(null);
                setInvoiceStatusFormContent(null);
              }, 300);
            }}
          />
          <div
            className={`preview-modal-container ${isClosingInvoiceModal ? "closing" : ""}`}
          >
            <div className="preview-modal-header">
              <h3 className="preview-modal-title">
                {selectedInvoiceRow
                  ? translate.ChangeIOStatus || "Cambiar estado de factura"
                  : translate.AIViewAdditionalFieldsDetail ||
                    "Vista de campos adicionales"}
              </h3>
              <button
                type="button"
                className="preview-modal-close"
                onClick={() => {
                  setIsClosingInvoiceModal(true);
                  setTimeout(() => {
                    setShowInvoiceStatusModal(false);
                    setIsClosingInvoiceModal(false);
                    setSelectedInvoiceRow(null);
                    setInvoiceStatusFormContent(null);
                  }, 300);
                }}
                title={translate.Close || "Cerrar"}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="preview-modal-content">
              {invoiceStatusFormContent || (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                  }}
                >
                  <i
                    className="fas fa-spinner-third fa-spin"
                    style={{ fontSize: 24, color: "#475467" }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de Edición de Tarea */}
      {(showTaskModal || isClosingTaskModal) && (
        <>
          <div
            className={`preview-modal-backdrop ${isClosingTaskModal ? "closing" : ""}`}
            onClick={() => {
              setIsClosingTaskModal(true);
              setTimeout(() => {
                setShowTaskModal(false);
                setIsClosingTaskModal(false);
                setEditingTaskIndex(null);
                setTaskFormContent(null);
              }, 300);
            }}
          />
          <div
            className={`preview-modal-container ${isClosingTaskModal ? "closing" : ""}`}
          >
            <div className="preview-modal-header">
              <h3 className="preview-modal-title">
                {translate.EditTask || translate.NewTask || "Editar tarea"}
              </h3>
              <button
                type="button"
                className="preview-modal-close"
                onClick={() => {
                  setIsClosingTaskModal(true);
                  setTimeout(() => {
                    setShowTaskModal(false);
                    setIsClosingTaskModal(false);
                    setEditingTaskIndex(null);
                    setTaskFormContent(null);
                  }, 300);
                }}
                title={translate.Close || "Cerrar"}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="preview-modal-content">
              {taskFormContent || (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                  }}
                >
                  <i
                    className="fas fa-spinner-third fa-spin"
                    style={{ fontSize: 24, color: "#475467" }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal de Preview */}
      {(showPreviewModal || isClosingModal) && (
        <>
          <div
            className={`preview-modal-backdrop ${isClosingModal ? "closing" : ""}`}
            onClick={() => {
              setIsClosingModal(true);
              setTimeout(() => {
                setShowPreviewModal(false);
                setIsClosingModal(false);
                setPreviewContent("");
              }, 300);
            }}
          />
          <div
            className={`preview-modal-container ${isClosingModal ? "closing" : ""}`}
          >
            <div className="preview-modal-header">
              <h3 className="preview-modal-title">
                {translate.EmailPreview || "Vista previa del email"}
              </h3>
              <button
                type="button"
                className="preview-modal-close"
                onClick={() => {
                  setIsClosingModal(true);
                  setTimeout(() => {
                    setShowPreviewModal(false);
                    setIsClosingModal(false);
                    setPreviewContent("");
                  }, 300);
                }}
                title={translate.Close || "Cerrar"}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="preview-modal-content">
              {previewContent ? (
                <SafeHtml html={previewContent} />
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%"
                  }}
                >
                  <i
                    className="fas fa-spinner-third fa-spin"
                    style={{ fontSize: 24, color: "#475467" }}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAgentEvaluatePopup;
